import React, { ChangeEvent, useEffect, useState } from "react"
import {
  TAchangePayment,
  TAupdatePaymentNote,
  TAuserPayments,
} from "../../../services/statisticsAPI"
import Filters from "./components/Filters"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye } from "@fortawesome/free-solid-svg-icons"
import StickyDataTable from "../../../components/StickyDataTable"
import * as XLSX from "xlsx"
import convertToCSV from "../../../utils/WaitingApprovalUserCSV"
import { UserPaymentFilters } from "./types"
import Modal from "../../../components/Modal"
import { getErrorStatus } from "../../../types/error"

const buildQueryParams = (filters: Record<string, string>) =>
  Object.entries(filters).reduce((acc, [key, value]) => {
    const trimmed = value?.trim()
    if (trimmed) {
      acc[key] = trimmed
    }
    return acc
  }, {} as Record<string, string>)

const bankingInformationLabelMap: Record<string, string> = {
  account_type: "Hesap Tipi",
  tax_exemption_document: "Vergi Muafiyet Belgesi",
  name: "Ad",
  surname: "Soyad",
  company_name: "Şirket Adı",
  tax_office: "Vergi Dairesi",
  tax_number: "Vergi Numarası",
  identity_number: "Kimlik Numarası",
  iban: "IBAN",
  city: "Şehir",
  district: "İlçe",
  address: "Adres",
}

const flattenBankingInformationForExport = (
  value: Record<string, unknown>,
  prefix = "Banka Bilgileri",
) =>
  Object.entries(value ?? {}).reduce(
    (acc, [key, rawValue]) => {
      const formattedKey =
        bankingInformationLabelMap[key] ??
        key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toLocaleUpperCase("tr-TR"))
      const exportKey = `${prefix} - ${formattedKey}`
      if (rawValue === null || rawValue === undefined) {
        acc[exportKey] = ""
        return acc
      }

      if (Array.isArray(rawValue)) {
        acc[exportKey] = rawValue
          .map((item) => (typeof item === "object" ? JSON.stringify(item) : String(item)))
          .join(", ")
        return acc
      }

      if (typeof rawValue === "object") {
        Object.assign(
          acc,
          flattenBankingInformationForExport(rawValue as Record<string, unknown>, exportKey),
        )
        return acc
      }

      if (
        typeof rawValue === "string" ||
        typeof rawValue === "number" ||
        typeof rawValue === "boolean"
      ) {
        acc[exportKey] = rawValue
        return acc
      }

      acc[exportKey] = String(rawValue)
      return acc
    },
    {} as Record<string, unknown>,
  )

const UserPayments = () => {
  const navigate = useNavigate()
  const [payments, setPayments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const [exporting, setExporting] = useState<null | "csv" | "xlsx">(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [mountByStatus, setMountByStatus] = useState({})
  const [sortByColumn, setSortByColumn] = useState<string>("")
  const [sortOrder, setSortOrder] = useState<boolean>(false)
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [query, setQuery] = useState<UserPaymentFilters>({
    dateStart: "",
    dateEnd: "",
    targetDateStart: "",
    targetDateEnd: "",
    paymentDateStart: "",
    paymentDateEnd: "",
    lastPaymentTryStart: "",
    lastPaymentTryEnd: "",
    name: "",
    surname: "",
    id_number: "",
    status: "",
  })
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [noteModalState, setNoteModalState] = useState<{
    isOpen: boolean
    paymentId: string | null
    note: string
  }>({
    isOpen: false,
    paymentId: null,
    note: "",
  })
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [confirmData, setConfirmData] = useState({
    open: false,
    id: "",
    status: "",
  })

  useEffect(() => {
    getPayments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, query, sortByColumn, sortOrder])

  useEffect(() => {
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sortByColumn, sortOrder])

  const getPayments = async () => {
    setError(false)
    setIsLoading(true)
    try {
      const response = await TAuserPayments(
        buildQueryParams(query),
        page,
        perPage,
        sortByColumn,
        sortOrder,
      )
      if (response) {
        const paymentsWithId = response.payments.map((item: any) => ({
          ...item,
        }))
        setPayments(paymentsWithId)
        setTotal(response.totalDocuments)
        setMountByStatus(response.mountByStatus || {})
      }
      setIsLoading(false)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(true)
      setIsLoading(false)
    }
  }

  const changePaymentState = async (payment_id: string, state: string) => {
    try {
      const response = await TAchangePayment(payment_id, state)
      if (response) {
        setPayments((prev: any) =>
          prev.map((item: any) => {
            if (item._id === payment_id) {
              return { ...item, status: state }
            }
            return item
          }),
        )
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(true)
    }
  }

  const handleSorting = (columnAccessor: string, direction?: "asc" | "desc") => {
    if (columnAccessor === sortByColumn) {
      setSortOrder(direction === "desc")
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(direction === "desc")
    }
  }

  const fetchPaymentsForExport = async () => {
    const perPageForExport = Math.max(total || perPage, 1)

    try {
      const response = await TAuserPayments(
        buildQueryParams({ ...query, isExport: "true" }),
        1,
        perPageForExport,
        sortByColumn,
        sortOrder,
      )
      if (response?.payments?.length) {
        return response.payments
      }
    } catch (exportError: unknown) {
      if (getErrorStatus(exportError) === 401) {
        navigate("/login")
      }
      // eslint-disable-next-line no-console -- surface export failures for debugging
      console.error("Failed to prepare export data", exportError)
    }

    return payments
  }

  const flattenForExport = (list: any[]) =>
    list.map((p: any) => {
      const bankingInformation = p.banking_information ?? {}
      const userAddress = p.user?.address ?? {}
      const userName = p.user?.name ?? ""
      const userSurname = p.user?.surname ?? ""
      const aliciAdi = `${userName} ${userSurname}`.trim()
      const tcNo =
        bankingInformation.identity_number ?? p.user?.money?.id ?? p.user?.id_number ?? ""
      const bankingCity = bankingInformation.city ?? ""
      const bankingDistrict = bankingInformation.district ?? ""
      const aliciAdres =
        bankingDistrict && bankingCity
          ? `${bankingDistrict}/${bankingCity}`.trim()
          : (bankingCity || bankingDistrict || userAddress.address) ?? userAddress.details ?? ""
      const aliciSehir = (bankingCity || userAddress.city) ?? p.user?.city ?? ""
      const aciklama =
        `TC ${tcNo} ${userName} ${userSurname} ${aliciAdres} İŞ BİRLİĞİ ÖDEMESİ`.trim()

      return {
        ID: p._id ?? "",
        "İŞLEM TARİHİ": p.payment_date
          ? new Date(p.payment_date).toLocaleString("tr-TR")
          : p.date
          ? new Date(p.date).toLocaleString("tr-TR")
          : "",
        "GÖNDEREN IBAN": "TR800006400000120240002710",
        "ALICI ADI": aliciAdi,
        TUTAR: p.mount ?? "",
        "ALICI IBAN": bankingInformation.iban ?? "",
        "ALICI ADRES": aliciAdres,
        "ALICI ŞEHİR": aliciSehir,
        AÇIKLAMA: aciklama,
        "TC / INFLUENCERDAN GELEN FATURA NO": tcNo,
        "E-mail": p.user?.email ?? "",
        Telefon: p.user?.phone ?? "",
        ...flattenBankingInformationForExport(bankingInformation),
      }
    })

  const handleExport = async (format: "csv" | "xlsx") => {
    setExportError(null)
    setExporting(format)
    try {
      const exportSource = await fetchPaymentsForExport()
      if (!exportSource.length) {
        setExportError("Dışa aktarılacak ödeme bulunamadı")
        return
      }
      const exportList = flattenForExport(exportSource)
      if (format === "csv") {
        const csv = convertToCSV(exportList)
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = "payments.csv"
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } else {
        const worksheet = XLSX.utils.json_to_sheet(exportList)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, "Payments")
        XLSX.writeFile(workbook, "payments.xlsx")
      }
    } catch (e) {
      setExportError("Dosya hazırlanırken bir hata oluştu")
    } finally {
      setExporting(null)
    }
  }

  const openNoteModal = (payment: any) => {
    setNoteModalState({
      isOpen: true,
      paymentId: payment._id,
      note: payment.note ?? "",
    })
  }

  const closeNoteModal = () => {
    setNoteModalState({
      isOpen: false,
      paymentId: null,
      note: "",
    })
  }

  const handleNoteChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target
    setNoteModalState((prev) => ({ ...prev, note: value }))
  }

  const handleSaveNote = async () => {
    if (!noteModalState.paymentId) {
      return
    }
    setIsSavingNote(true)
    try {
      const updatedPayment = await TAupdatePaymentNote(
        noteModalState.paymentId,
        noteModalState.note,
      )
      if (updatedPayment) {
        setPayments((prev: any) =>
          prev.map((item: any) =>
            item._id === updatedPayment._id ? { ...item, note: updatedPayment.note } : item,
          ),
        )
        closeNoteModal()
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(true)
    } finally {
      setIsSavingNote(false)
    }
  }

  const getConfirmMessage = (status: string) => {
    switch (status) {
      case "SUCCESS":
        return "Bu ödemenin başarıyla gerçekleştiğini onaylıyor musunuz?"
      case "FAILED":
        return "Bu ödemeyi başarısız olarak işaretlemek istediğinize emin misiniz?"
      case "PENDING":
        return "Bu ödemeyi beklemeye almak istediğinize emin misiniz?"
      default:
        return "Bu işlemi yapmak istediğinize emin misiniz?"
    }
  }

  const askConfirm = (id: string, status: string) => {
    setConfirmData({
      open: true,
      id,
      status,
    })
  }

  const handleConfirm = () => {
    changePaymentState(confirmData.id, confirmData.status)
    setConfirmData({ open: false, id: "", status: "" })
  }

  return (
    <div className="panel">
      {noteModalState.isOpen && (
        <Modal
          classname="fixed inset-0 bg-gray-800 bg-opacity-50 p-4 flex items-center justify-center z-50"
          classname2="relative bg-white rounded-3xl w-full max-w-lg"
          onClick={closeNoteModal}
        >
          <div className="p-6 space-y-4">
            <h2 className="text-2xl font-semibold text-gray-800">Notu Güncelle</h2>
            <textarea
              value={noteModalState.note}
              onChange={handleNoteChange}
              rows={6}
              className="w-full border border-gray-200 rounded-2xl p-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Notunuzu yazın"
            />
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-full border border-gray-300 text-gray-700"
                onClick={closeNoteModal}
                disabled={isSavingNote}
              >
                İptal
              </button>
              <button
                className="px-4 py-2 rounded-full bg-blue-600 text-white disabled:opacity-60"
                onClick={handleSaveNote}
                disabled={isSavingNote}
              >
                {isSavingNote ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex flex-col mt-2">
        {error && <div className="text-red-500">Bir hata oluştu</div>}

        {/* Payment Status Summary + Export Card */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(mountByStatus).map(([status, amount]) => (
            <div key={status} className="p-4 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 capitalize">
                    {status === "PENDING" && "Bekleyen"}
                    {status === "SUCCESS" && "Başarılı"}
                    {status === "DENIED" && "Reddedilen"}
                    {status === "FAILED" && "Başarısız"}
                    {status === "REQUESTED" && "Talep Edilen"}
                    {!["PENDING", "SUCCESS", "DENIED", "FAILED", "REQUESTED"].includes(status) &&
                      status}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {status} durumundaki ödemelerin toplam tutarı
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900">
                    {Number(amount).toLocaleString("tr-TR")} ₺
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Filters
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={query}
          setFilters={setQuery}
          handleFetchData={() => getPayments()}
        />
        <div className="flex flex-row gap-2">
          <div
            className="flex w-1/10 bg-gray-100 ml-4 items-center border border-gray-300 rounded-3xl mb-4 p-2 cursor-pointer"
            onClick={() => setIsFilterOpen(true)}
          >
            Filtrele
          </div>
          <div>
            <button
              className="px-4 py-2 rounded text-white bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed mr-2"
              onClick={() => handleExport("csv")}
              disabled={exporting !== null}
            >
              {exporting === "csv" && (
                <span className="inline-flex items-center mr-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                </span>
              )}
              CSV olarak indir
            </button>
            <button
              className="px-4 py-2 rounded text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={() => handleExport("xlsx")}
              disabled={exporting !== null}
            >
              {exporting === "xlsx" && (
                <span className="inline-flex items-center mr-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                </span>
              )}
              Excel olarak indir
            </button>
          </div>
        </div>
        {exportError && <div className="text-sm text-red-500 mt-2">{exportError}</div>}
      </div>
      <div className="datatables">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <StickyDataTable
            highlightOnHover={true}
            className="whitespace-nowrap"
            records={payments}
            columns={[
              {
                accessor: "id",
                title: "Id",
                sortable: true,
              },
              {
                accessor: "details",
                title: "Detaylar",
                sortable: true,
                render: ({ user }: any) => (
                  <a href={`/user/find/${user?._id}`} target="_blank" rel="noopener noreferrer">
                    <div className="text-center items-center">
                      <FontAwesomeIcon icon={faEye} style={{ color: "#005eff" }} />
                    </div>
                  </a>
                ),
              },
              {
                accessor: "profile_name",
                title: "Profil İsmi",
                sortable: true,
                render: (row: any) =>
                  row.user?.name || row.user?.surname
                    ? `${row.user?.name ?? ""} ${row.user?.surname ?? ""}`.trim()
                    : "",
              },
              {
                accessor: "email",
                title: "Email",
                sortable: true,
                render: (row: any) => row.user?.email,
              },
              {
                accessor: "phone",
                title: "Telefon",
                sortable: true,
                render: (row: any) => row.user?.phone,
              },
              {
                accessor: "address",
                title: "Şehir",
                sortable: true,
                render: (row: any) => row.banking_information?.city,
              },
              {
                accessor: "district",
                title: "İlçe",
                sortable: true,
                render: (row: any) => row.banking_information?.district,
              },
              { accessor: "account_type", title: "Hesap Tipi", sortable: true },
              { accessor: "account_id", title: "Hesap ID", sortable: true },
              {
                accessor: "banking_information",
                title: "IBAN",
                sortable: true,
                render: (row) =>
                  row.banking_information?.iban ? row.banking_information.iban : "-",
              },
              { accessor: "mount", title: "Miktar", sortable: true },
              { accessor: "currency", title: "Para Birimi", sortable: true },
              {
                accessor: "date",
                title: "Tarih (UTC+3)",
                sortable: true,
                render: (row) => (row.date ? new Date(row.date).toLocaleString("tr-TR") : "-"),
              },
              {
                accessor: "target_date",
                title: "Hedeflenen Tarih (UTC+3)",
                sortable: true,
                render: (row) =>
                  row.target_date ? new Date(row.target_date).toLocaleString("tr-TR") : "-",
              },
              {
                accessor: "payment_date",
                title: "Ödeme Tarihi (UTC+3)",
                sortable: true,
                render: (row) =>
                  row.payment_date ? new Date(row.payment_date).toLocaleString("tr-TR") : "-",
              },
              {
                accessor: "last_payment_try",
                title: "Son Ödeme Tarihi (UTC+3)",
                sortable: true,
                render: (row) =>
                  row.last_payment_try
                    ? new Date(row.last_payment_try).toLocaleString("tr-TR")
                    : "-",
              },
              { accessor: "status", title: "Durum", sortable: true },
              {
                accessor: "refund",
                title: "İade Durumu",
                sortable: true,
                render: (row) =>
                  row.refund
                    ? "İade Edildi"
                    : row.status === "FAILED" && row.refund === false
                    ? "İade Edilmedi"
                    : "-",
              },
              {
                accessor: "actions",
                title: "İşlem",
                sortable: true,
                render: (row) => (
                  <div className="flex gap-2">
                    {row.status === "REQUESTED" && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => askConfirm(row._id, "PENDING")}
                      >
                        Onayla
                      </button>
                    )}
                    {row.status === "PENDING" && (
                      <>
                        <button
                          className="px-3 py-1 rounded text-white bg-green-600 hover:bg-green-700 shadow"
                          onClick={() => askConfirm(row._id, "SUCCESS")}
                        >
                          Ödeme Yapıldı
                        </button>

                        <button
                          className="px-3 py-1 rounded text-white bg-red-600 hover:bg-red-700 shadow"
                          onClick={() => askConfirm(row._id, "FAILED")}
                        >
                          Ödeme Başarısız
                        </button>
                      </>
                    )}
                  </div>
                ),
              },
              {
                accessor: "message",
                title: "Hata Mesajı",
                sortable: true,
                render: (row) => row.message,
              },
              {
                accessor: "note",
                title: "Not",
                sortable: true,
                render: (row) => (
                  <div className="flex flex-col gap-2 max-w-xs">
                    <span className="text-sm text-gray-700 whitespace-pre-line break-words">
                      {row.note || "-"}
                    </span>
                    <button
                      className="text-blue-600 text-xs underline"
                      onClick={() => openNoteModal(row)}
                    >
                      {row.note ? "Notu Güncelle" : "Not Ekle"}
                    </button>
                  </div>
                ),
              },
            ]}
            totalRecords={total}
            recordsPerPage={perPage}
            page={page}
            onPageChange={(p) => setPage(p)}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={setPerPage}
            sortStatus={{
              columnAccessor: sortByColumn,
              direction: sortOrder ? "desc" : "asc",
            }}
            onSortStatusChange={(e) => handleSorting(e.columnAccessor, e.direction)}
            minHeight={200}
            paginationText={({ from, to, totalRecords }) =>
              `Showing  ${from} to ${to} of ${totalRecords} entries`
            }
          />
        )}
        {confirmData.open && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-xl w-80">
              <h2 className="text-lg font-semibold mb-4">Emin misiniz?</h2>
              <p className="mb-4">{getConfirmMessage(confirmData.status)}</p>

              <div className="flex justify-end gap-3">
                <button
                  className="px-3 py-1 rounded bg-gray-300 hover:bg-gray-400"
                  onClick={() => setConfirmData({ open: false, id: "", status: "" })}
                >
                  İptal
                </button>

                <button
                  className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleConfirm}
                >
                  Onayla
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserPayments
